/**
 * Componente global para toasts con el nuevo estilo
 * @module GlobalToast
 */
import React from 'react';
import { CheckCircle, AlertTriangle, Info, AlertCircle, XCircle } from 'lucide-react';
import { Toaster, toast } from 'react-hot-toast';

interface GlobalToastProps {
  position?: 'top-center' | 'top-right' | 'top-left' | 'bottom-center' | 'bottom-right' | 'bottom-left';
}

export const GlobalToast: React.FC<GlobalToastProps> = ({ 
  position = 'top-center' 
}) => {
  return (
    <Toaster
      position={position}
      reverseOrder={false}
      gutter={8}
      containerClassName="z-50"
      containerStyle={{}}
      toastOptions={{
        // Configuración por defecto
        duration: 4000,
        style: {
          background: '#fff',
          color: '#374151',
          padding: '16px',
          borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          border: '1px solid #e5e7eb',
          fontSize: '14px',
          fontWeight: '500',
          maxWidth: '400px',
          minWidth: '300px',
        },
        
        // Estilos personalizados por tipo
        success: {
          duration: 3000,
          style: {
            background: '#f0fdf4',
            color: '#166534',
            border: '1px solid #bbf7d0',
          },
          icon: <CheckCircle className="h-5 w-5 text-green-600" />,
          iconTheme: {
            primary: '#16a34a',
            secondary: '#f0fdf4',
          },
        },
        
        error: {
          duration: 5000,
          style: {
            background: '#fef2f2',
            color: '#dc2626',
            border: '1px solid #fecaca',
          },
          icon: <XCircle className="h-5 w-5 text-red-600" />,
          iconTheme: {
            primary: '#dc2626',
            secondary: '#fef2f2',
          },
        },
        
        loading: {
          duration: Infinity,
          style: {
            background: '#f8fafc',
            color: '#475569',
            border: '1px solid #e2e8f0',
          },
          iconTheme: {
            primary: '#3b82f6',
            secondary: '#f8fafc',
          },
        },
        
        // Toast personalizado para información
        custom: {
          duration: 4000,
          style: {
            background: '#eff6ff',
            color: '#1e40af',
            border: '1px solid #bfdbfe',
          },
          icon: <Info className="h-5 w-5 text-blue-600" />,
          iconTheme: {
            primary: '#3b82f6',
            secondary: '#eff6ff',
          },
        },
      }}
    />
  );
};

// Hook personalizado para usar los toasts con el nuevo estilo
export const useGlobalToast = () => {
  const showSuccess = (message: string) => {
    return toast.success(message);
  };

  const showError = (message: string) => {
    return toast.error(message);
  };

  const showInfo = (message: string) => {
    return toast(message, {
      icon: <Info className="h-5 w-5 text-blue-600" />,
      style: {
        background: '#eff6ff',
        color: '#1e40af',
        border: '1px solid #bfdbfe',
      },
    });
  };

  const showWarning = (message: string) => {
    return toast(message, {
      icon: <AlertTriangle className="h-5 w-5 text-yellow-600" />,
      style: {
        background: '#fffbeb',
        color: '#92400e',
        border: '1px solid #fed7aa',
      },
    });
  };

  const showLoading = (message: string) => {
    return toast.loading(message);
  };

  const dismiss = (toastId?: string) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  };

  return {
    showSuccess,
    showError,
    showInfo,
    showWarning,
    showLoading,
    dismiss,
  };
};
